import React, { useEffect, useState } from "react";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useHookstate, type State } from "@hookstate/core";
import { useLang } from "~/hooks/useLang";
import * as zod from 'zod';
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { deviceSetOnMission, getOnMissionReason } from "./services";
import notify from "~/utils/notify";
import { LoadingButton } from "@mui/lab";
import type { OnMissionReasonProps } from "./types";


interface onMissionDeviceProps {
  readonly openState: State<boolean>
  readonly identification: string
  readonly deviceId: number | null;
}

const OnMissionDeviceSchema = zod.object({
  onMissionReason: zod.number({ message: 'Informe um motivo' }),
  comment: zod.string({ message: 'Insira um comentário' })
    .min(5, { message: 'Minimo de 5 caracteres' }),
})

type OnMissionDeviceSchemaData = zod.infer<typeof OnMissionDeviceSchema>

export default function OnMissionDevice({ openState, identification, deviceId }: onMissionDeviceProps) {
  const { translate } = useLang();
  const loadingButton = useHookstate(false)
  const isLoading = useHookstate(false)
  const [optionsFilter, setOptionsFilter] = useState<readonly OnMissionReasonProps[]>([]);


  const { register, resetField, handleSubmit, control, setValue, clearErrors, formState: { errors } } = useForm<OnMissionDeviceSchemaData>({
    resolver: zodResolver(OnMissionDeviceSchema),
    criteriaMode: "all",
    defaultValues: {
      comment: '',
    },
  })
  useEffect(() => {
    resetValues()

    if (openState.get()) {
      isLoading.set(true)
      getOnMissionReason().then((response) => {
        setOptionsFilter(response)
      }).finally(() => {
        isLoading.set(false)
      })
    }

  }, [openState.get()])

  async function onSubmitForm(data: OnMissionDeviceSchemaData) {
    if (deviceId) {
      loadingButton.set(true)
      deviceSetOnMission({
        deviceId: deviceId,
        onMissionReasonId: data.onMissionReason,
        comment: data.comment
      })
        .then(({ status }) => {
          if (status === 200) {
            notify({ message: translate('Device moved to on mission Successfully!'), type: 'success' });
          }
          return handleCancel();
        }).finally(() => {
          loadingButton.set(false)
          // loadingRows?.set(true)
        });
    }
  }

  const resetValues = () => {
    resetField('comment')
    resetField('onMissionReason')

    clearErrors(['comment', 'onMissionReason'])
  };

  const handleCancel = () => {
    openState.set(false);
    resetValues();
  };

  return (
    <Dialog open={openState.get()} onClose={handleCancel} fullWidth={true}>
      <DialogTitle >
        {translate('On Mission')} - {identification}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent sx={{ padding: '0px 24px' }}>
          <Controller
            {...register('onMissionReason')}
            name={'onMissionReason'}
            control={control}
            render={(props) => (
              <Autocomplete
                {...props}
                id='filter-on-mission-reason'
                limitTags={2}
                options={optionsFilter}
                value={props.field.value && optionsFilter ? optionsFilter.find(element => element?.id === props.field.value) : null}
                noOptionsText={translate('No options')}
                clearText={translate('Clear')}
                size='small'
                loading={isLoading.get()}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name ?? ''}
                onChange={(_, type) => {
                  props.field.onChange(type?.id);
                  // dispatchGroupIdSelected.set(type?.id ? type.id : null)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${translate('On mission reason')} *`}
                    error={!!errors.onMissionReason?.type}
                    margin={'dense'}
                  />
                )} />
            )} />

          <Controller
            {...register('comment')}
            name={'comment'}
            control={control}
            render={({
              field: { onChange } }) => {
              return (
                <TextField
                  id='comment'
                  name={'comment'}
                  fullWidth
                  label={`${translate('Comment')} *`}
                  multiline
                  rows={4}
                  size='small'
                  sx={{ mt: 1 }}
                  error={!!errors.comment?.type}
                  onChange={({ target: { value } }) => {
                    onChange(value)
                    if (setValue) setValue('comment', value)
                  }}
                />
              )
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            gap: 1,
          }}
        >
          <Button variant='outlined' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <LoadingButton
            loading={loadingButton.get()}
            variant='contained'
            type='submit'
          >
            {translate("Confirm")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}